import React from 'react'

import { ScanResultIcon } from 'modules/resultsV2/ImportRequestScanDetails'
import {
  IMPORT_REQUEST_SCAN_DETAILS_TITLE,
  useImportRequestScanDetails
} from 'modules/resultsV2/hooks/useImportRequestScanDetails'
import { getImportRequestScanDetailsProps } from 'services/import-requests/importRequestUtils'
import type { ImportRequestVerificationDetail } from 'services/import-requests/types'
import { CardBody, CardHeading, RequestCard } from 'ui/card/card.styles'

import * as styles from './ImportRequestScanCard.styles'

export const IMPORT_REQUEST_SCAN_CARD_TEST_ID = 'import-request-scan-card'

/** A count the scan has not reported yet. */
const NOT_YET_KNOWN = '–'

/** Lets a test address a row by what it reports, rather than by walking the DOM. */
export const scanRowTestId = (label: string): string => `scan-row-${label.toLowerCase().replace(/\s+/g, '-')}`

export interface ImportRequestScanCardProps {
  importRequest: ImportRequestVerificationDetail
}

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <styles.Row data-testid={scanRowTestId(label)}>
    <styles.Label>{label}</styles.Label>
    {children}
  </styles.Row>
)

const CountRow = ({ label, count }: { label: string; count?: number }) => (
  <Row label={label}>
    <styles.Value>{count === undefined ? NOT_YET_KNOWN : count.toLocaleString()}</styles.Value>
  </Row>
)

/**
 * The scan sits beside the request rather than beneath it: a reviewer wants it in view while
 * reading the request, not after scrolling past it. Counts show a dash until the scan reports
 * them, so the shape of the card does not change as it progresses.
 */
export const ImportRequestScanCard = ({ importRequest }: ImportRequestScanCardProps): JSX.Element => {
  const { resolvedScanResult, scanResultMessage, formattedScanStartedAt, scanCounts, formattedTotalSize } =
    useImportRequestScanDetails(getImportRequestScanDetailsProps(importRequest))

  return (
    <RequestCard data-testid={IMPORT_REQUEST_SCAN_CARD_TEST_ID}>
      <CardBody>
        <CardHeading>{IMPORT_REQUEST_SCAN_DETAILS_TITLE}</CardHeading>

        <styles.Rows>
          <Row label="Status">
            <styles.StateValue $scanResult={resolvedScanResult}>
              <ScanResultIcon scanResult={resolvedScanResult} />
              <span>{scanResultMessage}</span>
            </styles.StateValue>
          </Row>
          <Row label="Scan start date">
            <styles.Value>{formattedScanStartedAt}</styles.Value>
          </Row>
          <CountRow label="Objects" count={scanCounts?.total} />
          <CountRow label="Clean" count={scanCounts?.clean} />
          <CountRow label="Threats found" count={scanCounts?.threatDetected} />
          <CountRow label="Unsupported" count={scanCounts?.unsupported} />
          <Row label="Total size">
            <styles.Value>
              {formattedTotalSize === undefined || formattedTotalSize === '' ? NOT_YET_KNOWN : formattedTotalSize}
            </styles.Value>
          </Row>
        </styles.Rows>
      </CardBody>
    </RequestCard>
  )
}
